import { useMemo } from 'react';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import useGetSchedulePreview from '@/features/dashboard/bookingChanges/hooks/useGetSchedulePreview.ts';
import useGetAffectedConsents from '@/features/dashboard/bookingChanges/hooks/useGetAffectedConsents.ts';
import useGetBookingDetails from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import useChangePackageNumber from '@/features/dashboard/bookingChanges/hooks/useChangePackageNumber.ts';
import { changeSchedulePreviewMapper } from '@/features/dashboard/bookingChanges/types/ChangeSchedulePreviewDto.mapper.ts';
import { ChangePackageSettlementVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';

/**
 * Krok W2 — rozkład dopłaty, warunki płatności korekty, trasa i dokumenty.
 *
 * Dokumenty to zgody wyjazdu, a te bierze się po `tourId` — którego trasa nie
 * zna, bo w ścieżce siedzą wyłącznie `bookingId` i `changeId`. `tourId` idzie
 * więc z `GET /api/bookings/{bookingId}`, tej samej metryczki, którą pobiera
 * już panel boczny; stamtąd bierze się też „wpłacono”, bo harmonogram mówi
 * wyłącznie o należności.
 */
const useChangePackageSettlement = (
  bookingId: string | undefined,
  changeId: string | undefined,
) => {
  const scheduleQuery = useGetSchedulePreview(bookingId, changeId);
  const bookingQuery = useGetBookingDetails(bookingId);
  const consentsQuery = useGetAffectedConsents(bookingQuery.data?.tourId);
  const packageNumberQuery = useChangePackageNumber(bookingId, changeId);
  const { packageNumber } = packageNumberQuery;

  // Saldo jest per waluta, więc „wpłacono” jest listą — rezerwacja potrafi
  // mieć wpłaty w dwóch walutach naraz.
  const paid: MoneyValueDto[] = useMemo(
    () =>
      bookingQuery.data?.balances.map((balance) => balance.balance.paid) ?? [],
    [bookingQuery.data],
  );

  const settlement: ChangePackageSettlementVm | null = useMemo(() => {
    // Odznaka kroku mówi „Paczka #13 · dopłata”, więc bez numeru nie ma czego
    // pokazać — ekran czeka na listę paczek, zamiast podstawiać liczbę
    // zastępczą.
    if (!scheduleQuery.data || !consentsQuery.data || packageNumber === null) {
      return null;
    }
    return changeSchedulePreviewMapper.toSettlementVm(
      scheduleQuery.data,
      consentsQuery.data,
      paid,
      packageNumber,
    );
  }, [scheduleQuery.data, consentsQuery.data, packageNumber, paid]);

  return {
    settlement,
    consents: consentsQuery.data ?? null,
    isLoading:
      scheduleQuery.isLoading ||
      bookingQuery.isLoading ||
      consentsQuery.isLoading ||
      packageNumberQuery.isLoading,
    isError:
      scheduleQuery.isError || bookingQuery.isError || consentsQuery.isError,
    /**
     * Podgląd potrafi rzucić walidacją transportu — operator ma wtedy zobaczyć
     * powód, a nie pusty ekran.
     */
    error: scheduleQuery.error ?? null,
  };
};

export default useChangePackageSettlement;
